import { useEffect, useState, type ReactNode } from "react"

import { ErroDaApi, type ProcedenciaDoCampo } from "../api"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { Button } from "../componentes/Button"
import { ComDica } from "../componentes/ComDica"
import { ConfirmarComSenha } from "../componentes/ConfirmarComSenha"
import * as Icones from "../componentes/Icones"
import { toast } from "../componentes/Toast"
import { GavetaDaFicha, RodapeDaGaveta } from "./Gaveta"
import { ACOES_DO_FORMULARIO, MAIUSCULA, RECUSA } from "../lib/classes"
import { cn } from "../lib/cn"

export function CampoEmLinha({
  rotulo,
  valor,
  procedencia,
  longo = false,
}: {
  rotulo: string
  valor: ReactNode
  procedencia?: ProcedenciaDoCampo
  longo?: boolean
}) {
  const vazio = valor === "—"
  return (
    <div
      data-slot="campo-em-linha"
      className={cn(
        "flex flex-col gap-1 border-b border-border py-3.5 text-sm last:border-b-0",
        procedencia?.origem === "agente" && "gap-1.5",
      )}
    >
      <div className={cn("flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1", longo && "flex-col items-stretch gap-1")}>
        <span data-slot="rotulo-do-campo" className="min-w-[min(180px,100%)] text-muted-foreground">
          {rotulo}
        </span>
        <span
          data-slot="valor-do-campo"
          className={cn(
            "max-w-full font-semibold tabular-nums wrap-anywhere",
            vazio ? "text-muted-foreground" : "text-foreground",
            longo ? "text-left" : "ml-auto text-right",
            MAIUSCULA,
          )}
        >
          {valor}
        </span>
      </div>
      {procedencia !== undefined && <Fundamento procedencia={procedencia} />}
    </div>
  )
}

const ORIGEM = "self-start text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-primary"

function rotuloDaOrigem(procedencia: ProcedenciaDoCampo): string {
  if (procedencia.origem === "athus") return "Origem: Processo Adm ATHUS"
  if (procedencia.origem === "agente") return "Origem: Documento do contrato"
  return procedencia.resumo
}

function Fundamento({ procedencia }: { procedencia: ProcedenciaDoCampo }) {
  const [aberto, definirAberto] = useState(false)
  const rotulo = rotuloDaOrigem(procedencia)
  const temTrecho = procedencia.como_foi_lido !== null
  if (procedencia.origem !== "agente" && !temTrecho) {
    if (rotulo === "") return null
    return procedencia.dica === null ? (
      <span className={cn(ORIGEM, "")}>{rotulo}</span>
    ) : (
      <ComDica dica={procedencia.dica} classe={ORIGEM}>
        {rotulo}
      </ComDica>
    )
  }
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {procedencia.origem === "agente" && (
          <span className="inline-flex items-center gap-1 rounded-sm bg-muted px-2 py-1.5 text-[0.75rem] font-medium text-foreground">
            <span className="flex text-muted-foreground">
              <Icones.Fundamento tamanho={12} />
            </span>
            Lido no documento — confira
          </span>
        )}
        {temTrecho ? (
          <button
            type="button"
            aria-expanded={aberto}
            className="inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
            onClick={() => definirAberto(!aberto)}
          >
            <span>{rotulo}</span>
            <span className={cn("flex", aberto ? "text-primary" : "text-muted-foreground")}>
              {aberto ? <Icones.Diminuir tamanho={14} /> : <Icones.Aumentar tamanho={14} />}
            </span>
          </button>
        ) : (
          <ComDica dica={procedencia.dica} classe={ORIGEM}>
            {rotulo}
          </ComDica>
        )}
      </div>
      {temTrecho && aberto && <ComoFoiLido texto={procedencia.como_foi_lido ?? ""} />}
    </>
  )
}

function ComoFoiLido({ texto }: { texto: string }) {
  const partes = /^([\s\S]*?)(?: Trecho do documento: (“[\s\S]*?”))?(?: Motivo: ([\s\S]*))?$/.exec(texto)
  const frase = partes?.[1] ?? texto
  const trecho = partes?.[2]
  const motivo = partes?.[3]
  return (
    <div data-slot="como-foi-lido" className="mt-2 rounded-md bg-muted p-3 text-xs leading-relaxed text-muted-foreground">
      <p className="mb-1 font-medium text-foreground">{frase}</p>
      {trecho !== undefined && (
        <p data-slot="trecho-do-documento" className="rounded-sm border border-border bg-card p-2 font-mono text-[0.6875rem] text-muted-foreground italic">
          {trecho}
        </p>
      )}
      {motivo !== undefined && <p className={cn("mt-1", MAIUSCULA)}>Motivo: {motivo}</p>}
    </div>
  )
}

export class RecusaJaNaTela extends Error {}

export function BlocoEditavel({
  titulo,
  recado,
  editavel = true,
  motivoSemEdicao,
  acao,
  leitura,
  formulario,
  aoGravar,
  falta = null,
  inclui = false,
  pedeSenha,
  edicao = "no-bloco",
  tituloDaGaveta,
  mensagemDeSucesso,
  pedidoDeEdicao = 0,
  aoAtenderPedido,
}: {
  titulo: string
  recado?: ReactNode
  editavel?: boolean
  motivoSemEdicao?: string
  acao?: ReactNode
  leitura: ReactNode
  formulario?: (fechar: () => void) => ReactNode
  aoGravar?: (senha: string) => Promise<void>
  falta?: string | null
  inclui?: boolean
  pedeSenha?: { titulo: string; resumo?: ReactNode; rotuloDoAto: string }
  edicao?: "no-bloco" | "gaveta"
  tituloDaGaveta?: string
  mensagemDeSucesso?: string
  pedidoDeEdicao?: number
  aoAtenderPedido?: () => void
}) {
  const [editando, definirEditando] = useState(false)
  const naGaveta = edicao === "gaveta"
  const sucesso = mensagemDeSucesso ?? (inclui ? `${titulo}: registro adicionado` : `${titulo}: alterações salvas`)

  useEffect(() => {
    if (pedidoDeEdicao > 0 && editavel && formulario !== undefined) {
      definirEditando(true)
      aoAtenderPedido?.()
    }
  }, [pedidoDeEdicao])
  const [pedindoSenha, definirPedindoSenha] = useState(false)
  const [erro, definirErro] = useState<string | null>(null)
  const [gravando, definirGravando] = useState(false)

  const gravar = async () => {
    if (aoGravar === undefined) return
    if (pedeSenha !== undefined) {
      definirErro(null)
      definirPedindoSenha(true)
      return
    }
    definirGravando(true)
    definirErro(null)
    try {
      await aoGravar("")
      definirEditando(false)
      if (naGaveta) toast.success(sucesso)
    } catch (falha: unknown) {
      if (!(falha instanceof RecusaJaNaTela)) {
        definirErro(mensagemDaFalha(falha))
        if (naGaveta) toast.error(mensagemDaFalha(falha))
      }
    } finally {
      definirGravando(false)
    }
  }

  const fechar = () => {
    if (gravando) return
    definirErro(null)
    definirEditando(false)
  }

  if (naGaveta) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {titulo}
            {typeof recado === "string" && (
              <>
                {" "}
                <ComDica dica={recado} />
              </>
            )}
          </CardTitle>
          {(acao !== undefined || (editavel && formulario !== undefined) || !editavel) && (
            <CardAction>
              <div className="flex items-center gap-2">
                {acao}
                {editavel && formulario !== undefined && (
                  <Button
                    size="sm"
                    type="button"
                    onClick={() => {
                      definirErro(null)
                      definirEditando(true)
                    }}
                  >
                    {inclui ? <Icones.Mais /> : <Icones.Anotacao />}
                    {inclui ? "Adicionar" : "Editar"}
                  </Button>
                )}
                {!editavel && motivoSemEdicao !== undefined && (
                  <span data-slot="motivo-sem-edicao" className={cn("text-xs text-muted-foreground", MAIUSCULA)}>
                    {motivoSemEdicao}
                  </span>
                )}
              </div>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          {recado !== undefined && typeof recado !== "string" && (
            <p className="mb-3 max-w-[78ch] text-sm text-muted-foreground">{recado}</p>
          )}
          {leitura}
        </CardContent>
        {formulario !== undefined && (
          <GavetaDaFicha
            aberta={editando}
            titulo={tituloDaGaveta ?? `${inclui ? "Adicionar" : "Editar"}: ${titulo}`}
            aoFechar={fechar}
          >
            {erro !== null && (
              <p role="alert" className={cn(RECUSA, MAIUSCULA)}>
                {erro}
              </p>
            )}
            {formulario(fechar)}
            <RodapeDaGaveta aoCancelar={fechar} ocupado={gravando}>
              <Button
                type="button"
                title={falta ?? undefined}
                disabled={gravando || falta !== null}
                onClick={() => void gravar()}
              >
                <Icones.Salvar />
                {gravando ? "Salvando..." : "Salvar"}
              </Button>
            </RodapeDaGaveta>
            {pedeSenha !== undefined && aoGravar !== undefined && (
              <ConfirmarComSenha
                aberto={pedindoSenha}
                titulo={pedeSenha.titulo}
                resumo={pedeSenha.resumo}
                rotuloDoAto={pedeSenha.rotuloDoAto}
                iconeDoAto={<Icones.Salvar />}
                aoConfirmar={(senha) =>
                  aoGravar(senha).then(() => {
                    definirEditando(false)
                    toast.success(sucesso)
                  })
                }
                aoFechar={() => definirPedindoSenha(false)}
              />
            )}
          </GavetaDaFicha>
        )}
      </Card>
    )
  }

  const temAlgoNaCabeca =
    (!editando && (acao !== undefined || (editavel && formulario !== undefined))) || !editavel

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {titulo}
          {typeof recado === "string" && (
            <>
              {" "}
              <ComDica dica={recado} />
            </>
          )}
        </CardTitle>
        {temAlgoNaCabeca && (
          <CardAction>
            <div className="flex items-center gap-2">
              {!editando && acao}
              {!editando && editavel && formulario !== undefined && (
                <Button
                  size="sm"
                  type="button"
                  onClick={() => {
                    definirErro(null)
                    definirEditando(true)
                  }}
                >
                  {inclui ? <Icones.Mais /> : <Icones.Anotacao />}
                  {inclui ? "Adicionar" : "Editar"}
                </Button>
              )}
              {!editavel && motivoSemEdicao !== undefined && (
                <span data-slot="motivo-sem-edicao" className={cn("text-xs text-muted-foreground", MAIUSCULA)}>
                  {motivoSemEdicao}
                </span>
              )}
            </div>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {recado !== undefined && typeof recado !== "string" && (
          <p className="mb-3 max-w-[78ch] text-sm text-muted-foreground">{recado}</p>
        )}
        {erro !== null && <p className={cn(RECUSA, MAIUSCULA)}>{erro}</p>}
        {editando && formulario !== undefined ? (
          <>
            {formulario(() => definirEditando(false))}
            <div className={cn(ACOES_DO_FORMULARIO, "justify-end")}>
              <Button
                variant="outline"
                size="sm"
                className="border-destructive text-destructive hover:text-destructive"
                type="button"
                disabled={gravando}
                onClick={() => {
                  definirErro(null)
                  definirEditando(false)
                }}
              >
                <Icones.Fechar />
                Cancelar
              </Button>
              <Button
                size="sm"
                className="bg-status-sucesso text-sobre-status-sucesso hover:bg-status-sucesso-pressionado"
                title={falta ?? undefined}
                type="button"
                disabled={gravando || falta !== null}
                onClick={() => void gravar()}
              >
                <Icones.Salvar />
                {gravando ? "Salvando..." : "Salvar"}
              </Button>
            </div>
            {pedeSenha !== undefined && aoGravar !== undefined && (
              <ConfirmarComSenha
                aberto={pedindoSenha}
                titulo={pedeSenha.titulo}
                resumo={pedeSenha.resumo}
                rotuloDoAto={pedeSenha.rotuloDoAto}
                iconeDoAto={<Icones.Salvar />}
                aoConfirmar={(senha) => aoGravar(senha).then(() => definirEditando(false))}
                aoFechar={() => definirPedindoSenha(false)}
              />
            )}
          </>
        ) : (
          leitura
        )}
      </CardContent>
    </Card>
  )
}

export function mensagemDaFalha(falha: unknown): string {
  if (falha instanceof ErroDaApi)
    return falha.acao !== "" ? `${falha.regra}. ${falha.acao}` : falha.regra
  return falha instanceof Error ? falha.message : "não foi possível salvar"
}

export function procedenciaDe(
  lista: ProcedenciaDoCampo[],
  campo: string,
): ProcedenciaDoCampo | undefined {
  return lista.find((p) => p.campo === campo)
}
